import { useRef } from 'react';

import { Piece, PieceAnnotationPagedList, PiecePagedList, Status, Workflow } from '@apiContract';
import times from 'lodash/fp/times';
import { useAuth } from 'src/routes/Auth/authContext';
import { PromiseQueue } from 'src/utils/promiseQueue';

import { getCompanyFromHost } from '@utils/url';

import { CurrentSessionProps } from '../types';
import { ActionTypes, Actions } from './reducer';

const ITEMS_PER_PAGE = 100;

const createPieceSearchParams = (
  receiveSessionId: string,
  itemsPerPage: number = ITEMS_PER_PAGE,
  pageNumber: number = 0,
): URLSearchParams => {
  const searchParams = new URLSearchParams();
  searchParams.append('Filter.ReceiveSessionId', receiveSessionId);
  searchParams.append('Paging.ItemsPerPage', itemsPerPage.toString());
  searchParams.append('Paging.PageNumber', pageNumber.toString());
  [Status.Active, Status.Draft].forEach((status) => {
    searchParams.append('Filter.Statuses', status.toString());
  });
  return searchParams;
};

const createAnnotationSearchParams = (receiveSessionId: string): URLSearchParams => {
  return new URLSearchParams({
    ReceiveSessionId: receiveSessionId,
  });
};

const createRequestOptions = (accessToken: string | undefined): RequestInit => {
  return {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'X-Company-Subdomain': getCompanyFromHost(),
    },
  };
};

export const useReceiveSessionItems = (
  sessionProps: CurrentSessionProps,
  setItems: (items: Piece[]) => void,
  dispatch: React.Dispatch<Actions>,
) => {
  const { user } = useAuth();
  const accessToken = user?.access_token;

  const loaded = useRef(false);

  const onPiecesLoaded = () => {
    if (
      sessionProps.receiveSession &&
      [Workflow.Lockers, Workflow.Carrier, Workflow.Location].includes(sessionProps.receiveSession.workflow)
    ) {
      const url = new URL(`${import.meta.env.VITE_APP_API}/api/piece/annotations`);
      const params = createAnnotationSearchParams(sessionProps.receiveSession.id.toString());
      url.search = params.toString();

      fetch(url, createRequestOptions(accessToken)).then(async (response) => {
        const { items } = (await response.json()).objectData as PieceAnnotationPagedList;
        dispatch({ type: ActionTypes.SET_PIECE_ANNOTATIONS, payload: items || [] });
      });
    }
  };

  const loadPiecese = async () => {
    loaded.current = true;

    if (!sessionProps || !sessionProps.receiveSession || !sessionProps.receiveSession.id) return;

    const { totalPiecesCount, id } = sessionProps.receiveSession;
    const baseUrl = new URL(`${import.meta.env.VITE_APP_API}/api/Piece`);
    const pages = totalPiecesCount ? Math.ceil(totalPiecesCount / ITEMS_PER_PAGE) : 0;
    const tasks: (() => Promise<undefined>)[] = [];

    times(
      (i) =>
        tasks.push(async () => {
          const url = new URL(baseUrl);
          url.search = createPieceSearchParams(id.toString(), ITEMS_PER_PAGE, i).toString();

          const response = await fetch(url, createRequestOptions(accessToken));
          const responseItems = (await response.json()).objectData as PiecePagedList;
          setItems(responseItems.items || []);
        }),
      pages,
    );

    const queue = new PromiseQueue(tasks, onPiecesLoaded);
    queue.run();
  };

  if (
    sessionProps &&
    !sessionProps.isNew &&
    sessionProps.receiveSession &&
    sessionProps.receiveSession.totalPiecesCount &&
    !loaded.current
  ) {
    loadPiecese();
  }
};
